import React, { FC, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { useMetaTraderTopic } from '@webtrader/core/hooks/order/useMetaTraderTopic';
import { usePublicTopic } from '@webtrader/core/hooks/public/usePublicTopic';
import { useUserTopic } from '@webtrader/core/hooks/user/useUserTopic';
import lazy from '@webtrader/core/lazy';
import { ALL_MOBILE_ROUTES } from '@webtrader/core/router/mobile';
import { useAuthWithKeys } from '@webtrader/core/services/auth';
import { paymentService } from '@webtrader/core/services/payment';
import { statisticsService } from '@webtrader/core/services/statistics';

import { BaseSafeAreaView } from '@/components/Screen/SafeAreaView';

const AssetsScreen = lazy(() => import('@/screens/assets'));
const HomeScreen = lazy(() => import('@/screens/home'));
const InfoScreen = lazy(() => import('@/screens/info'));
const MarketScreen = lazy(() => import('@/screens/market'));
const TradeScreen = lazy(() => import('@/screens/trade'));

const AccountNavigator = lazy(() => import('./Account'));
const CopyTradeScreen = lazy(() => import('@/screens/copytrade'));
const EditOrderScreen = lazy(() => import('@/screens/order/Edit'));
const NotificationsScreen = lazy(() => import('@/screens/notifications'));
const NotificationOrderDetailScreen = lazy(() => import('@/screens/notifications/OrderDetail'));
const SearchScreen = lazy(() => import('@/screens/search'));
const SignalScreen = lazy(() => import('@/screens/signal'));
const TransactionsScreen = lazy(() => import('@/screens/transaction'));
const DemoCreationScreen = lazy(() => import('@/screens/auth/DemoCreation'));
const DemoCompletionScreen = lazy(() => import('@/screens/auth/DemoCompletion'));
const OneClickTradingTncScreen = lazy(() => import('@/screens/account/OneClickTermsAndConditions'));
const OneClickTradingConfirmationScreen = lazy(() => import('@/screens/trade/Symbol/OneClickConfirmation'));

const BottomTabBar = lazy(() => import('./BottomTab/Bar'));

interface IProps {}

const MainNavigation: FC<IProps> = () => {
  const { selectedTradingAccount } = useAuthWithKeys(['selectedTradingAccount']);
  const { traderSubscription } = selectedTradingAccount || {};

  useMetaTraderTopic(traderSubscription);
  usePublicTopic();
  useUserTopic(traderSubscription);

  useEffect(() => {
    paymentService.init();
    statisticsService.init();
  }, []);

  return (
    <BaseSafeAreaView>
      <Switch>
        {/* Bottom Tab Routes */}
        <Route path={ALL_MOBILE_ROUTES.BOTTOM_TAB.INDEX} component={HomeScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.BOTTOM_TAB.ASSETS} component={AssetsScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.BOTTOM_TAB.MARKETS} component={MarketScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.BOTTOM_TAB.TRADE} component={TradeScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.BOTTOM_TAB.INFO} component={InfoScreen} exact={true} />

        <Route path={ALL_MOBILE_ROUTES.MAIN.ACCOUNT} component={AccountNavigator} />
        <Route path={ALL_MOBILE_ROUTES.MAIN.SIGNAL} component={SignalScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.MAIN.SEARCH} component={SearchScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.MAIN.NOTIFICATIONS} component={NotificationsScreen} exact={true} />
        <Route
          path={ALL_MOBILE_ROUTES.MAIN.NOTIFICATIONS_ORDER}
          component={NotificationOrderDetailScreen}
          exact={true}
        />
        <Route path={ALL_MOBILE_ROUTES.MAIN.TRANSACTION} component={TransactionsScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.MAIN.EDIT_ORDER} component={EditOrderScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.MAIN.COPY_TRADE} component={CopyTradeScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.MAIN.DEMO_CREATION} component={DemoCreationScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.MAIN.DEMO_COMPLETION} component={DemoCompletionScreen} exact={true} />
        <Route path={ALL_MOBILE_ROUTES.MAIN.ONE_CLICK_TRADING_TNC} component={OneClickTradingTncScreen} exact={true} />
        <Route
          path={ALL_MOBILE_ROUTES.MAIN.ONE_CLICK_TRADING_CONFIRMATION}
          component={OneClickTradingConfirmationScreen}
          exact={true}
        />

        <Redirect to={ALL_MOBILE_ROUTES.BOTTOM_TAB.INDEX} />
      </Switch>
      <BottomTabBar />
    </BaseSafeAreaView>
  );
};

export default MainNavigation;
